<template>
  <div class="exchange-rate exchange-rate-content">
    <div class="rate-source" role="group" :aria-label="$t('exchangeRates.sourceLabel')">
        <button
            type="button"
            class="rate-source__btn"
            :class="{ active: source === 'imex' }"
            @click="$emit('update:source', 'imex')"
        >{{ $t('exchangeRates.sourceImex') }}</button>
        <button
            type="button"
            class="rate-source__btn"
            :class="{ active: source !== 'imex' }"
            :title="$t('exchangeRates.sourceOtherHint')"
            @click="$emit('update:source', 'other')"
        >{{ $t('exchangeRates.sourceOther') }}</button>
    </div>
    <div class="exchange-rate-control">
        <div class="rate-check" v-if="!isMobile">
            <UICheckbox :value="usdRub" :small="true" @click="usdRub = !usdRub"/>
            <span>{{ usdLabel }}</span>
        </div>
        <div class="rate-check"  v-if="!isMobile">
            <UICheckbox :value="btcUsd" :small="true" @click="btcUsd = !btcUsd"/>
            <span>BTC/USDT</span>
        </div>
        <div class="rate-check"  v-if="!isMobile">
            <UICheckbox :value="btcRub" :small="true" @click="btcRub = !btcRub"/>
            <span>BTC/RUB</span>
        </div>
        <UIDownload
            class="exchange-rate-control__download"
            :disabled="true"
            @onDownload="onDownload"
        />
    </div>
    <div class="exchange-rate-data">
        <div class="exchange-rate-table-header">
            <span class="exchange-rate-item-content"><b>{{ $t('wallet.date') }}</b></span>
            <span class="exchange-rate-item-content" v-if="!isMobile && usdRub"><b>{{ usdLabel }}</b></span>
            <span class="exchange-rate-item-content" v-if="!isMobile && btcUsd"><b>BTC/USDT</b></span>
            <span class="exchange-rate-item-content" v-if="btcRub"><b>BTC/RUB</b></span>
        </div>
        <div v-for="rate of rates" :key="rate.date" class="exchange-rate-table-row">
            <span class="exchange-rate-item-content">{{rate.date}}</span>
            <span class="exchange-rate-item-content" v-if="!isMobile && usdRub">{{rate.usdRub}}</span>
            <span class="exchange-rate-item-content" v-if="!isMobile && btcUsd">{{rate.btcUsdt}}</span>
            <span class="exchange-rate-item-content" v-if="btcRub">{{rate.btcRub}}<template v-if="rate.btcRubDerived">*</template></span>
        </div>
        <p v-if="loading" class="exchange-rate-note">{{ $t('exchangeRates.loading') }}</p>
        <p v-else-if="!rates.length" class="exchange-rate-note">
            {{ source === 'imex' ? $t('exchangeRates.noImexData') : $t('exchangeRates.noData') }}
        </p>
        <p v-if="hasDerived" class="exchange-rate-note">{{ $t('exchangeRates.derivedNote') }}</p>
    </div>
  </div>
</template>

<script>
import UICheckbox from "@/components/ui/checkbox/checkbox.vue";
import UIDownload from "@/components/ui/download/download.vue";

export default {
  name: "ExchangeRate",
  components: {
    UICheckbox,
    UIDownload
  },
  props: {
      rates: {
          type: Array,
          default: () => [],
      },
      // "imex" - курсы IMEX (показываются первыми), "other" - Bybit + ЦБ РФ (по запросу пользователя)
      source: {
          type: String,
          default: 'imex',
      },
      loading: {
          type: Boolean,
          default: false,
      },
  },
  emits: ['update:source'],
  computed: {
      // У IMEX колонка usdRub - это курс USDT/RUB (пара IMEX/RUB), у прочих источников - USD/RUB (ЦБ РФ)
      usdLabel() {
          return this.source === 'imex' ? this.$t('exchangeRates.usdtRub') : 'USD/RUB'
      },
      hasDerived() {
          return this.source === 'imex' && this.rates.some(r => r.btcRubDerived)
      },
  },
  data() {
    return {
        isMobile: false,
        usdRub: true,
        btcUsd: true,
        btcRub: true
    }
  },
  mounted() {
    if (window.innerWidth <= 768) {
        this.isMobile = true
    }
  }
}
</script>

<style lang="scss">
.exchange-rate {
    display: flex;
    flex-direction: column;
    grid-row-gap: 16px;
    position: relative;
}

.exchange-rate-content {
    background: $white;
    border-radius: 0px 16px 16px 16px;
    padding: 22px 20px 44px 20px;
    display: flex;
    flex-direction: column;
    grid-row-gap: 34px;
}

.rate-source {
    display: flex;
    grid-column-gap: 8px;
}

.rate-source__btn {
    border: 1px solid $bg-gray;
    background: $white;
    color: $txt-main;
    border-radius: 10px;
    padding: 8px 16px;
    cursor: pointer;
    transition: $transition;

    &:hover {
        border-color: $main;
    }

    &.active {
        background: $main;
        border-color: $main;
        color: $white;
    }
}

.exchange-rate-note {
    margin: 12px 5px 0;
    color: $txt-light;
    font-size: 14px;
}

.exchange-rate-control {
    display: flex;
}

.rate-check {
    display: flex;
    align-items: center;
    margin: 0px 10px;
}

.exchange-rate-control__download {
    margin-left: auto;
}

.exchange-rate-table-header {
    background: $bg-gray;
    border-radius: 10px;
    padding: 10px 5px;
    display: grid;
    grid-template-columns: repeat(auto-fill, 25%);
}

.exchange-rate-table-row {
    padding: 6px 5px;
    display: grid;
    grid-template-columns: repeat(auto-fill, 25%);
    border-radius: 10px;

    &:nth-child(2n) {
        background: $bg-gray-light;
    }
}

.exchange-rate-item-content {
    white-space: nowrap;
}

@media screen and (max-width: 768px) {
    .exchange-rate-control {
        display: flex;
        flex-direction: column;
    }

    .rate-check {
        display: flex;
        align-items: center;
        margin: 3px 0px;
    }

    .exchange-rate-table-header {
        background: $bg-gray;
        border-radius: 10px;
        padding: 10px 5px;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
    }

    .exchange-rate-table-row {
        padding: 6px 5px;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        border-radius: 10px;

        &:nth-child(2n) {
            background: $bg-gray-light;
        }
    }
}
</style>